import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://education-crm-2b77a26760ff.herokuapp.com/api';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Attach JWT token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('nel_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}, (err) => Promise.reject(err));

// Handle 401 globally
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('nel_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ——— Auth ———
export const authAPI = {
  login:           (data)         => api.post('/auth/login', data),
  register:        (data)         => api.post('/auth/register', data),
  me:              ()             => api.get('/auth/me'),
  changePassword:  (data)         => api.put('/auth/password', data),
  updateProfile:   (data)         => api.put('/auth/profile', data),
  forgotPassword:  (email)        => api.post('/auth/forgot-password', { email }),
  resetPassword:   (token, data)  => api.post(`/auth/reset-password/${token}`, data),
};

// ——— Settings ———
export const settingsAPI = {
  getEmailSettings:    ()       => api.get('/settings/email'),
  updateEmailSettings: (data)   => api.put('/settings/email', data),
  sendTestEmail:       (email)  => api.post('/settings/email/test', { recipient_email: email }),
  getEmailStatus:      ()       => api.get('/settings/email/status'),
};

// ——— WhatsApp ———
export const whatsappAPI = {
  getSettings:   ()       => api.get('/settings/whatsapp'),
  updateSettings:(data)   => api.put('/settings/whatsapp', data),
  sendTest:      (phone)  => api.post('/settings/whatsapp/test', { phone }),
  getStatus:     ()       => api.get('/settings/whatsapp/status'),
  getLogs:       (params) => api.get('/settings/whatsapp/logs', { params }),
  getLogStats:   ()       => api.get('/settings/whatsapp/logs/stats'),
  sendToLead:    (id, data) => api.post(`/leads/${id}/whatsapp`, data),
  sendToStudent: (id, data) => api.post(`/students/${id}/whatsapp`, data),
};

// ——— Revenue ———
export const revenueAPI = {
  getEvents:   (params) => api.get('/revenue/events', { params }),
  createEvent: (data)   => api.post('/revenue/events', data),
  getEvent:    (id)     => api.get(`/revenue/events/${id}`),
  updateEvent: (id, d)  => api.put(`/revenue/events/${id}`, d),
  deleteEvent: (id)     => api.delete(`/revenue/events/${id}`),
  getDashboard:()       => api.get('/revenue/dashboard'),
  getMyEarnings:()      => api.get('/revenue/dashboard/my-earnings'),
  getLeaderboard:(params) => api.get('/revenue/leaderboard', { params }),
};

// ——— Commission ———
export const commissionAPI = {
  getConfigs:       ()          => api.get('/commission/config'),
  getConfig:        (userId)    => api.get(`/commission/config/${userId}`),
  upsertConfig:     (userId, d) => api.post(`/commission/config/${userId}`, d),
  deleteConfig:     (userId)    => api.delete(`/commission/config/${userId}`),
  getEntries:       (params)    => api.get('/commission/entries', { params }),
  getEntrySummary:  (params)    => api.get('/commission/entries/summary', { params }),
  calculateEntries: (data)      => api.post('/commission/entries/calculate', data),
  approveEntry:     (id)        => api.put(`/commission/entries/${id}/approve`),
  markEntryPaid:    (id, data)  => api.put(`/commission/entries/${id}/mark-paid`, data),
  holdEntry:        (id, data)  => api.put(`/commission/entries/${id}/hold`, data),
  getTargets:       (params)    => api.get('/commission/targets', { params }),
  getTarget:        (uid, m, y) => api.get(`/commission/targets/${uid}/${m}/${y}`),
  setTarget:        (uid, m, y, d) => api.post(`/commission/targets/${uid}/${m}/${y}`, d),
  finalizeTarget:   (uid, m, y) => api.put(`/commission/targets/${uid}/${m}/${y}/finalize`),
  calcQuarterly:    (data)      => api.post('/commission/quarterly-bonus/calculate', data),
};

// ——— Leads ———
export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  checkDuplicate: (params) => api.get('/leads/check-duplicate', { params }),
  runSentiment: (id) => api.post(`/leads/${id}/sentiment`),
  importCSV: (formData) => api.post('/leads/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportCSV: (params) => api.get('/leads/export', { params, responseType: 'blob' }),
  bulkAssign: (data) => api.post('/leads/bulk-assign', data),
  getActivityLog: (id, params) => api.get(`/leads/${id}/activity`, { params }),
  getAudio: (id) => api.get(`/leads/${id}/audio`),
  uploadAudio: (id, formData) => api.post(`/leads/${id}/audio`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// ——— Follow-ups ———
export const followupsAPI = {
  getByLead: (leadId) => api.get(`/followups/lead/${leadId}`),
  create: (data) => api.post('/followups', data),
  complete: (id, data) => api.put(`/followups/${id}/complete`, data),
  update: (id, data) => api.put(`/followups/${id}`, data),
  delete: (id) => api.delete(`/followups/${id}`),
  getOverdue: () => api.get('/followups/overdue'),
};

// ——— Tasks ———
export const tasksAPI = {
  getTasks:          (params)          => api.get('/tasks', { params }),
  getTask:           (id)              => api.get('/tasks/' + id),
  createTask:        (data)            => api.post('/tasks', data),
  updateTask:        (id, data)        => api.put('/tasks/' + id, data),
  deleteTask:        (id)              => api.delete('/tasks/' + id),
  updateStatus:      (id, status)      => api.put('/tasks/' + id + '/status', { status }),
  completeTask:      (id)              => api.put('/tasks/' + id + '/complete'),
  getMyTasks:        (params)          => api.get('/tasks/my-tasks', { params }),
  getStats:          ()                => api.get('/tasks/stats'),
  getCategories:     ()                => api.get('/tasks/categories'),
  addSubtask:        (id, data)        => api.post('/tasks/' + id + '/subtasks', data),
  updateSubtask:     (id, sid, data)   => api.put('/tasks/' + id + '/subtasks/' + sid, data),
  deleteSubtask:     (id, sid)         => api.delete('/tasks/' + id + '/subtasks/' + sid),
  completeSubtask:   (id, sid)         => api.put('/tasks/' + id + '/subtasks/' + sid + '/complete'),
  addUpdate:         (id, data)        => api.post('/tasks/' + id + '/updates', data),
  getUpdates:        (id)              => api.get('/tasks/' + id + '/updates'),
  uploadAttachment:  (id, formData)    => api.post('/tasks/' + id + '/attachments', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadAttachment:(id, aid)         => api.get('/tasks/' + id + '/attachments/' + aid + '/download', { responseType: 'blob' }),
  viewAttachment:    (id, aid)         => api.get('/tasks/' + id + '/attachments/' + aid + '/view',     { responseType: 'blob' }),
  deleteAttachment:  (id, aid)         => api.delete('/tasks/' + id + '/attachments/' + aid),
  logTime:           (id, data)        => api.post('/tasks/' + id + '/timelogs', data),
  getTimeLogs:       (id)              => api.get('/tasks/' + id + '/timelogs'),
  updateTimeLog:     (id, lid, data)   => api.put('/tasks/' + id + '/timelogs/' + lid, data),
  deleteTimeLog:     (id, lid)         => api.delete('/tasks/' + id + '/timelogs/' + lid),
  getReportStats:    (params)          => api.get('/tasks/report-stats', { params }),
  exportCSV:         ()                => api.get('/tasks/export-csv', { responseType: 'blob' }),
  // Legacy (used by lead/student detail tabs)
  getByLead:         (leadId)          => api.get('/tasks/lead/' + leadId),
};

// ——— Meetings ———
export const meetingsAPI = {
  getByLead: (leadId) => api.get(`/meetings/lead/${leadId}`),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  delete: (id) => api.delete(`/meetings/${id}`),
};

// ——— Documents ———
export const documentsAPI = {
  getByLead: (leadId) => api.get(`/documents/lead/${leadId}`),
  upload: (formData) => api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  view: (docId) => api.get(`/documents/${docId}/view`, { responseType: 'blob' }),
  download: (docId) => api.get(`/documents/${docId}/download`, { responseType: 'blob' }),
};

// ——— Notifications ———
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`)
};

// ——— Users ———
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  deactivate: (id) => api.delete(`/users/${id}`),
};

// ——— Reports ———
export const reportsAPI = {
  getDashboard: (params) => api.get('/reports/dashboard', { params }),
  getPipeline: () => api.get('/reports/pipeline'),
  export: (params) => api.get('/reports/export', { params, responseType: 'blob' })
};

// ——— Contacts (embedded in Lead) ———
export const contactsAPI = {
  add:    (leadId, data)            => api.post(`/leads/${leadId}/contacts`, data),
  update: (leadId, contactId, data) => api.put(`/leads/${leadId}/contacts/${contactId}`, data),
  remove: (leadId, contactId)       => api.delete(`/leads/${leadId}/contacts/${contactId}`),
};

// ——— AI ———
export const aiAPI = {
  generateContent: (data) => api.post('/leads/ai/generate', data),
  analyzeSentiment: (leadId) => api.post(`/leads/${leadId}/sentiment`),
  tabAssist: (data) => api.post('/leads/ai/tab-assist', data),
};

// ——— Funnel ———
export const funnelAPI = {
  trackView:        (slug, params) => api.get(`/funnel/${slug}/track-view`, { params }),
  submitFranchise:  (data)         => api.post('/funnel/franchise-apply', data),
  submitStudent:    (data)         => api.post('/funnel/student-interest', data),
  submitInstitution:(data)         => api.post('/funnel/institutional-inquiry', data),
  getPages:         ()             => api.get('/funnel/pages'),
  createPage:       (data)         => api.post('/funnel/pages', data),
  updatePage:       (id, data)     => api.put(`/funnel/pages/${id}`, data),
  getPageAnalytics: (id)           => api.get(`/funnel/pages/${id}/analytics`),
  getOverview:      ()             => api.get('/funnel/analytics/overview'),
  getBySource:      ()             => api.get('/funnel/analytics/by-source'),
  getConversion:    ()             => api.get('/funnel/analytics/conversion'),
};

// ——— Students ———
export const studentsAPI = {
  getAll:    (params)      => api.get('/students', { params }),
  getById:   (id)          => api.get(`/students/${id}`),
  update:    (id, data)    => api.put(`/students/${id}`, data),
  getStats:  ()            => api.get('/students/stats/summary'),
  manualAdd: (data)        => api.post('/students/manual-add', data),
};

// ——— Partners ———
export const partnersAPI = {
  getMyProfile:    ()       => api.get('/partners/my'),
  updateMyProfile: (data)   => api.put('/partners/my', data),
  getOutreachKit:  ()       => api.get('/partners/outreach-kit'),
  getAllProfiles:   ()       => api.get('/partners/all'),
};

// ——— Objection Playbook ———
export const objectionsAPI = {
  // Library (admin)
  getAll:    ()           => api.get('/objections'),
  getAllAdmin:()           => api.get('/objections/all'),
  create:    (data)       => api.post('/objections', data),
  update:    (id, data)   => api.put(`/objections/${id}`, data),

  // Lead objection log
  getByLead: (leadId)             => api.get(`/objections/lead/${leadId}`),
  log:       (leadId, data)       => api.post(`/objections/lead/${leadId}`, data),
  generate:  (leadId, logId)      => api.post(`/objections/lead/${leadId}/${logId}/generate`),
  updateLog: (leadId, logId, data)=> api.put(`/objections/lead/${leadId}/${logId}`, data),
};

// ——— Knowledge Base ———
export const knowledgeAPI = {
  upload:          (formData)  => api.post('/knowledge/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getDocuments:    (params)    => api.get('/knowledge/documents', { params }),
  getDocument:     (id)        => api.get(`/knowledge/documents/${id}`),
  updateDocument:  (id, data)  => api.put(`/knowledge/documents/${id}`, data),
  deleteDocument:  (id)        => api.delete(`/knowledge/documents/${id}`),
  reindexDocument: (id)        => api.post(`/knowledge/documents/${id}/reindex`),
  reindexMultiple: (ids)       => api.post('/knowledge/documents/reindex-multiple', { document_ids: ids }),
  indexSelected:   (ids)       => api.post('/knowledge/documents/index-selected', { document_ids: ids }),
  getStatus:       (ids)       => api.get('/knowledge/documents/status', { params: { ids: ids.join(',') } }),
  search:          (params)    => api.get('/knowledge/search', { params }),
  getStats:        ()          => api.get('/knowledge/stats'),
};

export default api;
